/**
 * Demo mock data for the Fortune 500 executive demo.
 * All content is hardcoded and tailored for enterprise decision-makers.
 */

// Typing sequence with intentional typo and correction
// Format: { char: string, action: 'type' | 'delete' | 'pause', duration?: number }
export interface TypingAction {
  char: string;
  action: 'type' | 'delete' | 'pause';
  duration?: number; // Override default timing
}

// The user query that will be "typed" with natural typos
export const DEMO_USER_QUERY = "Our executives need insights from 5 years of sales data across 12 regional offices. We're looking for an AI solution that can handle enterprise-scale analytics with SOC 2 compliance.";

// Generate typing sequence with a typo in "executives" -> "exectuives"
export function generateTypingSequence(): TypingAction[] {
  const sequence: TypingAction[] = [];
  
  // Type "Our exectuives" (with typo)
  const typoText = "Our exectuives";
  for (const char of typoText) {
    sequence.push({ char, action: 'type' });
  }
  
  // Pause to "notice" the mistake
  sequence.push({ char: '', action: 'pause', duration: 400 });
  
  // Delete "ives" (4 characters)
  for (let i = 0; i < 4; i++) {
    sequence.push({ char: '', action: 'delete', duration: 80 });
  }
  
  // Pause briefly
  sequence.push({ char: '', action: 'pause', duration: 200 });
  
  // Type correction "utives"
  const correction = "utives";
  for (const char of correction) {
    sequence.push({ char, action: 'type' });
  }
  
  // Continue with rest of the query
  const restOfQuery = " need insights from 5 years of sales data across 12 regional offices. We're looking for an AI solution that can handle enterprise-scale analytics with SOC 2 compliance.";
  for (const char of restOfQuery) {
    sequence.push({ char, action: 'type' });
  }
  
  return sequence;
}

// Reasoning steps shown during AI "thinking"
export interface ReasoningStep {
  id: string;
  text: string;
  duration: number; // How long this step takes
  icon: 'search' | 'brain' | 'database' | 'sparkles' | 'chart';
}

export const REASONING_STEPS: ReasoningStep[] = [
  {
    id: 'understand',
    text: 'Understanding your data analytics requirements...',
    duration: 3000,
    icon: 'brain',
  },
  {
    id: 'search',
    text: 'Searching across 847 vendors in our knowledge base...',
    duration: 5000,
    icon: 'search',
  },
  {
    id: 'analyze',
    text: 'Analyzing vendor capabilities against enterprise needs...',
    duration: 4500,
    icon: 'database',
  },
  {
    id: 'compliance',
    text: 'Verifying SOC 2 compliance certifications...',
    duration: 3500,
    icon: 'chart',
  },
  {
    id: 'visualize',
    text: 'Generating interactive visualization using MCP Apps...',
    duration: 4000,
    icon: 'sparkles',
  },
];

// Vendor data for the report cards
export interface VendorResult {
  id: string;
  name: string;
  logo: string; // URL or emoji placeholder
  tagline: string;
  matchScore: number; // 0-100
  capabilities: string[];
  enterpriseFeatures: string[];
  pricingTier: 'Enterprise' | 'Premium' | 'Custom';
  whyRecommended: string;
  considerations: string[];
  deploymentTime: string;
  fortune500Clients: number;
}

export const VENDOR_RESULTS: VendorResult[] = [
  {
    id: 'glean',
    name: 'Glean',
    logo: '🔍',
    tagline: 'Enterprise AI Search & Knowledge',
    matchScore: 96,
    capabilities: [
      'Unified Enterprise Search',
      '100+ App Connectors',
      'AI-Powered Answers',
      'Knowledge Graph',
    ],
    enterpriseFeatures: [
      'SOC 2 Type II Certified',
      'SSO & RBAC',
      'Data Residency Controls',
      'Audit Logging',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'Glean connects 100+ enterprise applications into a unified knowledge layer. Organizations drowning in information silos across Slack, Drive, Confluence, and Jira see immediate productivity gains. Search quality is best-in-class and onboarding is frictionless.',
    considerations: [
      'AI pipeline is opaque—no visibility into prompts or model selection',
      'Proprietary knowledge graph creates lock-in',
      'Limited transferable AI skills for your team',
    ],
    deploymentTime: '4-8 weeks',
    fortune500Clients: 200,
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    logo: '❄️',
    tagline: 'AI-Powered Data Cloud',
    matchScore: 95,
    capabilities: [
      'Cortex AI Functions',
      'Cross-Cloud Data Sharing',
      'Natural Language Queries',
      'Auto-scaling Compute',
    ],
    enterpriseFeatures: [
      'SOC 2 Type II Certified',
      'FedRAMP Authorized',
      'End-to-end Encryption',
      'Role-based Access Control',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'Snowflake\'s separation of storage and compute means regional offices can query data simultaneously without performance degradation. Cortex AI enables natural language queries—executives can ask questions in plain English against years of sales data.',
    considerations: [
      'Credit-based pricing requires monitoring',
      'AI features still maturing vs. dedicated ML platforms',
      'Data egress costs for multi-cloud',
    ],
    deploymentTime: '6-10 weeks',
    fortune500Clients: 186,
  },
  {
    id: 'retool',
    name: 'Retool',
    logo: '⚡',
    tagline: 'Low-Code + AI Internal Tools',
    matchScore: 94,
    capabilities: [
      'AI-Assisted UI Building',
      'Database & API Connectors',
      'Workflow Automation',
      'Custom AI Agents',
    ],
    enterpriseFeatures: [
      'SOC 2 Type II Certified',
      'SSO & Audit Logs',
      'Self-Hosted Option',
      'Row-Level Security',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'Retool accelerates internal tool development with AI-assisted code generation. Build CRUD apps, dashboards, and workflows 10x faster. AI agents can automate complex internal processes without writing custom code.',
    considerations: [
      'Requires technical team for advanced customization',
      'Pricing scales with seats and AI usage',
      'Ecosystem lock-in for workflows',
    ],
    deploymentTime: '2-4 weeks',
    fortune500Clients: 120,
  },
  {
    id: 'windsurf',
    name: 'Windsurf',
    logo: '🌊',
    tagline: 'AI-Powered Coding Assistant',
    matchScore: 93,
    capabilities: [
      'Inline Code Generation',
      'Multi-File Context',
      'Terminal & Chat Integration',
      'Codebase-Aware Suggestions',
    ],
    enterpriseFeatures: [
      'On-Premise Deployment Option',
      'Code Privacy Controls',
      'Team Analytics',
      'Custom Model Support',
    ],
    pricingTier: 'Premium',
    whyRecommended: 'Windsurf (Codeium) brings AI pair programming to your engineering team. Context-aware code generation, multi-file edits, and integrated terminal make it a strong alternative to GitHub Copilot for teams wanting flexibility.',
    considerations: [
      'Newer entrant—enterprise maturity evolving',
      'Requires team adoption and workflow changes',
      'Model quality varies by language and framework',
    ],
    deploymentTime: '1-2 weeks',
    fortune500Clients: 45,
  },
  {
    id: 'canva',
    name: 'Canva',
    logo: '🎨',
    tagline: 'Visual Design + AI',
    matchScore: 92,
    capabilities: [
      'Magic Design AI',
      'Brand Kits',
      'Collaborative Editing',
      'Template Library',
    ],
    enterpriseFeatures: [
      'SOC 2 Type II Certified',
      'SSO & SCIM',
      'Brand Controls',
      'Usage Analytics',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'Canva democratizes design with AI-powered templates and Magic Design. Marketing and comms teams can produce professional assets without dedicated designers. Enterprise features ensure brand consistency at scale.',
    considerations: [
      'Advanced design needs may require Adobe',
      'AI output quality varies by use case',
      'Storage limits on lower tiers',
    ],
    deploymentTime: '1-2 weeks',
    fortune500Clients: 95,
  },
  {
    id: 'pymc',
    name: 'PyMC',
    logo: '📊',
    tagline: 'Bayesian Modeling & AI',
    matchScore: 91,
    capabilities: [
      'Probabilistic Programming',
      'Bayesian Inference',
      'Uncertainty Quantification',
      'PyMC Labs Support',
    ],
    enterpriseFeatures: [
      'Open Source Core',
      'Enterprise Consulting',
      'Training & Workshops',
      'Custom Model Support',
    ],
    pricingTier: 'Custom',
    whyRecommended: 'PyMC (PyMC Labs) brings rigorous Bayesian modeling to AI decisions. When you need uncertainty quantification, A/B test analysis, or risk modeling—not just point estimates—PyMC provides the statistical foundation.',
    considerations: [
      'Requires statistical/ML expertise',
      'Custom pricing for enterprise support',
      'Smaller ecosystem than PyTorch/TensorFlow',
    ],
    deploymentTime: '8-16 weeks',
    fortune500Clients: 35,
  },
  {
    id: 'credo-ai',
    name: 'Credo AI',
    logo: '🛡️',
    tagline: 'AI Governance & Risk',
    matchScore: 90,
    capabilities: [
      'AI Risk Assessment',
      'Model Documentation',
      'Compliance Frameworks',
      'Bias Monitoring',
    ],
    enterpriseFeatures: [
      'SOC 2 Type II Certified',
      'EU AI Act Ready',
      'Audit Trail',
      'Policy Engine',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'Credo AI helps enterprises govern AI systems at scale. As regulations like the EU AI Act advance, having model documentation, risk scores, and compliance workflows in one platform reduces legal and reputational risk.',
    considerations: [
      'Governance overhead may slow iteration',
      'Platform integration complexity',
      'Nascent market—product evolving',
    ],
    deploymentTime: '6-12 weeks',
    fortune500Clients: 55,
  },
  {
    id: 'factored-ai',
    name: 'Factored AI',
    logo: '👥',
    tagline: 'AI-Powered Data Workforce',
    matchScore: 89,
    capabilities: [
      'ML Data Labeling',
      'Model Training Support',
      'Data Operations',
      'Quality Assurance',
    ],
    enterpriseFeatures: [
      'NDA & Security Protocols',
      'Quality SLAs',
      'Scalable Teams',
      'Custom Workflows',
    ],
    pricingTier: 'Custom',
    whyRecommended: 'Factored AI provides trained data professionals for ML pipelines. When you need high-quality labeled data, model evaluation, or human-in-the-loop operations at scale, they offer flexible staffing with AI expertise.',
    considerations: [
      'Offshore model—time zone coordination',
      'Quality depends on task specification',
      'Custom pricing per project',
    ],
    deploymentTime: '2-6 weeks',
    fortune500Clients: 40,
  },
  {
    id: 'evidenza',
    name: 'Evidenza',
    logo: '📈',
    tagline: 'AI for Market Research',
    matchScore: 88,
    capabilities: [
      'AI Survey Analysis',
      'Sentiment & Theme Extraction',
      'Auto-Reporting',
      'Integration with Research Tools',
    ],
    enterpriseFeatures: [
      'Data Privacy Compliant',
      'Secure Data Handling',
      'API Access',
      'Custom Models',
    ],
    pricingTier: 'Premium',
    whyRecommended: 'Evidenza accelerates market research with AI-powered analysis. Turn survey responses, interviews, and open-ended data into actionable insights. Reduces manual coding and speeds time-to-insight for insights teams.',
    considerations: [
      'Best for survey/qualitative data',
      'Integration with legacy research tools',
      'Emerging vendor—track record growing',
    ],
    deploymentTime: '4-8 weeks',
    fortune500Clients: 30,
  },
  {
    id: 'synthetic-users',
    name: 'Synthetic Users',
    logo: '🧪',
    tagline: 'AI User Testing',
    matchScore: 87,
    capabilities: [
      'AI Test Participants',
      'Usability Feedback',
      'Survey Generation',
      'Behavior Simulation',
    ],
    enterpriseFeatures: [
      'Privacy by Design',
      'Custom Personas',
      'API Integration',
      'Detailed Reporting',
    ],
    pricingTier: 'Premium',
    whyRecommended: 'Synthetic Users provides AI-powered user testing without recruiting real participants. Validate designs, prototypes, and flows faster. Useful for early-stage validation when human participants are costly or slow.',
    considerations: [
      'AI personas may not reflect real user diversity',
      'Best combined with human testing',
      'Niche use case—validate fit',
    ],
    deploymentTime: '1-2 weeks',
    fortune500Clients: 25,
  },
  {
    id: 'ipsos',
    name: 'Ipsos (AI Offerings)',
    logo: '🌐',
    tagline: 'Market Research + AI',
    matchScore: 89,
    capabilities: [
      'AI-Powered Insights',
      'Predictive Analytics',
      'Automated Reporting',
      'Global Panel Access',
    ],
    enterpriseFeatures: [
      'SOC 2 Certified',
      'GDPR Compliant',
      'Global Data Collection',
      'Enterprise Support',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'Ipsos combines decades of market research expertise with AI-powered analysis. For Fortune 500 brands needing brand tracking, concept testing, or consumer intelligence at scale, their AI offerings accelerate insight delivery.',
    considerations: [
      'AI is additive to core research services',
      'Pricing is project-based',
      'Long sales cycles for enterprise',
    ],
    deploymentTime: '8-16 weeks',
    fortune500Clients: 180,
  },
  {
    id: 'nielseniq',
    name: 'NielsenIQ (AI Offerings)',
    logo: '📊',
    tagline: 'Retail Analytics + AI',
    matchScore: 88,
    capabilities: [
      'AI Demand Forecasting',
      'Shelf Analytics',
      'Consumer Intelligence',
      'Retail Execution',
    ],
    enterpriseFeatures: [
      'SOC 2 Type II Certified',
      'Global Coverage',
      'Retail Data Integration',
      'Custom Dashboards',
    ],
    pricingTier: 'Enterprise',
    whyRecommended: 'NielsenIQ brings AI to retail and CPG analytics. Predict demand, optimize assortments, and understand consumer behavior with AI-enhanced data. Proven at major retailers and brands worldwide.',
    considerations: [
      'Enterprise pricing—significant commitment',
      'Data integration complexity',
      'AI features vary by product line',
    ],
    deploymentTime: '12-24 weeks',
    fortune500Clients: 150,
  },
];

// AI response header
export const AI_RESPONSE_HEADER = "I've identified 12 enterprise-grade AI vendors that match your requirements. Based on your needs for multi-regional access and executive insights, here's my analysis:";

// Summary statistics for the report header
export const REPORT_STATS = {
  vendorsAnalyzed: 847,
  matchingVendors: 23,
  topRecommendations: 12,
  avgMatchScore: 91,
};
